import $ from 'jquery';

/**
 * Form Table of Contents.
 */
const FormToc = function ($anchorContainer) {
    const formToc = this;

    // Initialize intersection observer
    formToc.intersectionObserver = new IntersectionObserver(
        entries => formToc.handleIntersectionObserver(entries),
        {rootMargin: '-100px 0px'},
    );

    // Set anchor and section elements
    formToc.$anchorContainer = $anchorContainer;
    formToc.$anchors = $anchorContainer.find('a');
    formToc.$visibleSections = $();
    formToc.$activeSection = $();
    formToc.$sections = formToc.getSectionsFromAnchors();

    // Apply initial active section
    formToc.applyActiveSection();

    // Handle changes in responsive layout
    $(window).on('resize', () => formToc.applyActiveSection());
};

/**
 * Get the sections of this table of contents from its anchors.
 *
 * @return {JQuery}
 */
FormToc.prototype.getSectionsFromAnchors = function () {
    const formToc = this;

    const sectionList = [];

    formToc.$anchors.each(function () {
        const $anchor = $(this);
        const $section = $(this.hash).first();

        if (! $section.length) {
            return;
        }

        // Add section to the list
        sectionList.push($section[0]);

        // Bind anchor to section
        $section.data('formTocAnchor', this);

        // Handle events on anchor and section
        $anchor.on('click', () => formToc.setActiveSection($section));
        $section.on('focusin mousedown', () => formToc.setActiveSection($section))

        // Add section to intersection observer
        formToc.intersectionObserver.observe($section[0]);

        // Set active section from URL
        if (this.hash === location.hash) {
            formToc.$activeSection = $section;
        }
    });

    return $(sectionList);
};

/**
 * Handle IntersectionObserver API.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver
 *
 * @param {Array<IntersectionObserverEntry>} entries
 */
FormToc.prototype.handleIntersectionObserver = function (entries) {
    const formToc = this;

    // Determine which sections were added to view and which ones were removed from view
    const addedToView = entries.filter(entry => entry.isIntersecting).map(entry => entry.target);
    const removedFromView = entries.filter(entry => ! entry.isIntersecting).map(entry => entry.target);

    // Update the set of visible sections accordingly
    formToc.$visibleSections = formToc.$visibleSections.not(removedFromView).add(addedToView);

    // Update active section
    let hasActiveSection = formToc.$activeSection.length > 0;
    if (hasActiveSection && formToc.$activeSection.is(removedFromView)) {
        // Active section was removed from view
        hasActiveSection = false;

        // If active element in DOM is in the active section that was removed from view, unfocus it
        const $activeElement = $(document.activeElement);
        if (document.activeElement && $.contains(formToc.$activeSection[0], document.activeElement)) {
            $activeElement.trigger('blur');
            if ($activeElement.data('daterangepicker')) {
                $activeElement.data('daterangepicker').hide();
            }
        }
    }

    // Find and set a new active section
    if (! hasActiveSection) {
        formToc.setActiveSection(formToc.findNearestVisibleSection());
    }
};

/**
 * Find the visible section that is the nearest to current active section.
 *
 * @return {JQuery}
 */
FormToc.prototype.findNearestVisibleSection = function () {
    const formToc = this;

    const activeRect = formToc.$activeSection.length
        ? formToc.$activeSection[0].getBoundingClientRect()
        : new DOMRectReadOnly();

    let nearest = {distance: Infinity};

    formToc.$visibleSections.each(function () {
        const rect =  this.getBoundingClientRect();
        const x = Math.max(0, Math.max(activeRect.left, rect.left) - Math.min(activeRect.right, rect.right));
        const y = Math.max(0, Math.max(activeRect.top, rect.top) - Math.min(activeRect.bottom, rect.bottom));
        const distance = x * x + y * y;

        if (distance < nearest.distance) {
            nearest = {distance: distance, section: this};
        }
    });

    return $(nearest.section);
};

/**
 * Set active section.
 *
 * @param {JQuery} $activeSection
 */
FormToc.prototype.setActiveSection = function ($activeSection) {
    const formToc = this;

    formToc.$activeSection = $activeSection;
    formToc.applyActiveSection();
};

/**
 * Apply changes to active section.
 */
FormToc.prototype.applyActiveSection = function () {
    const formToc = this;
    let activeHash = '';

    formToc.$sections.add(formToc.$anchors).removeClass('active');

    if (formToc.$activeSection.length) {
        const activeAnchor = formToc.$activeSection.data('formTocAnchor');

        formToc.$activeSection.addClass('active');
        activeHash = activeAnchor.hash;

        if (formToc.shouldShowActiveAnchor()) {
            $(activeAnchor).addClass('active');
        }
    }

    if (location.hash !== activeHash) {
        history.replaceState(null, '', location.pathname + location.search + activeHash);
    }
};

/**
 * Should active anchor be shown in current responsive layout?
 *
 * @return {boolean}
 */
FormToc.prototype.shouldShowActiveAnchor = function () {
    const formToc = this;

    return formToc.$anchorContainer.children().css('position') === 'sticky';
};

/**
 * JQuery interface.
 */
$.fn.formToc = function () {
    return this.each(function () {
        const $anchorContainer = $(this);

        if (! $anchorContainer.data('formToc')) {
            $anchorContainer.data('formToc', new FormToc($anchorContainer));
        }
    });
};

/**
 * Auto initialize on load from class "form-toc".
 */
$(function () {
    $('.form-toc').formToc();
});

export default FormToc;
